import type { DocumentRecord, DocumentRef } from "@agency/core";
import { useMutation, useQuery } from "@tanstack/react-query";
import { FileText } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { callAction } from "../api";
import { ModeBadge } from "../components/ModeBadge";
import { useToast } from "../components/Toast";
import { Button, Card, EmptyState, ErrorState, LoadingState, Select } from "../components/ui";
import { useLocale, useT } from "../i18n";
import { formatBytes, shortDate } from "../theme";

const KINDS = ["application_summary", "acord_form", "id_card", "certificate", "dec_page", "upload", "export"] as const;

export function DocumentsScreen() {
  const t = useT();
  const { locale } = useLocale();
  const { toast } = useToast();
  const [kind, setKind] = useState("");
  const q = useQuery({
    queryKey: ["documents", kind],
    queryFn: async () => (await callAction<"agency.document.list", DocumentRecord[]>("agency.document.list", { kind: kind || undefined, limit: 500 })).result,
  });
  const open = useMutation({
    mutationFn: (documentId: string) => callAction<"agency.document.get", DocumentRef>("agency.document.get", { documentId }),
    onSuccess: (r) => window.open(r.result.url, "_blank", "noopener"),
    onError: (e) => toast({ title: t("common.error"), body: (e as Error).message, tone: "error" }),
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold">{t("documents.title")}</h1>
          <p className="text-ink/60">{t("documents.subtitle")}</p>
        </div>
        <Select aria-label={t("common.kind")} value={kind} onChange={(e) => setKind(e.target.value)} className="sm:w-56">
          <option value="">{t("common.all")}</option>
          {KINDS.map((k) => <option key={k} value={k}>{t(`documents.kind.${k}`)}</option>)}
        </Select>
      </div>
      {q.isLoading ? <LoadingState /> : null}
      {q.isError ? <ErrorState error={q.error} onRetry={() => q.refetch()} /> : null}
      {q.data && q.data.length === 0 ? <EmptyState title={t("documents.none")} body={t("documents.licensed")} /> : null}
      {q.data && q.data.length > 0 ? (
        <Card>
          <ul className="divide-y divide-ink/5">
            {q.data.map((doc) => (
              <li key={doc.id} className="flex items-center gap-3 px-5 py-3">
                <FileText className="h-5 w-5 text-ink/40 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold truncate">{doc.title}</p>
                  <p className="text-xs text-ink/55 truncate">
                    {t(`documents.kind.${doc.kind}`)}{doc.formEdition ? ` · ${doc.formEdition}` : ""} · {shortDate(doc.createdAt, locale)} · {formatBytes(doc.sizeBytes)} · <Link to={`/customers/${doc.customerId}`} className="hover:underline">{t("common.customer")} →</Link>
                  </p>
                </div>
                <ModeBadge mode={doc.executionMode} />
                <Button variant="outline" size="sm" onClick={() => open.mutate(doc.id)}>{t("common.open")}</Button>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}
    </div>
  );
}
